// servicesData.js — Full detailed data for all 6 services (Updated)

export const servicesData = [
  {
    id: "ai-supply-chain",
    slug: "ai-supply-chain",
    title: "Supply Chain Management",
    tagline: "Intelligent, Self-Optimizing Supply Chains",
    shortDesc:
      "Leverage AI and machine learning to transform your supply chain into a predictive, resilient, and autonomous network that reduces costs and improves service levels.",
    heroImage:
      "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1200&auto=format&fit=crop&q=80",
    icon: "🧠",
    color: "sky",
    overview: {
      heading: "AI-Powered Supply Chain Transformation",
      body: `Traditional supply chains are reactive, fragmented, and prone to disruption. AI-driven supply chain management uses machine learning, predictive analytics, and intelligent automation to create a self-optimizing ecosystem.

From demand sensing and dynamic inventory allocation to autonomous logistics and supplier risk prediction, AI enables real-time decision-making at scale. AgileTech helps you embed AI into every link of your supply chain – procurement, planning, warehousing, transportation, and last-mile delivery – unlocking unprecedented efficiency and resilience.`,
    },
    stats: [
      { value: "45%", label: "Reduction in Stockouts" },
      { value: "30%", label: "Lower Logistics Costs" },
      { value: "90%", label: "Forecast Accuracy Improvement" },
      { value: "250+", label: "AI Supply Chains Deployed" },
    ],
    features: [
      {
        title: "AI Demand Sensing & Forecasting",
        desc: "Real-time demand prediction using external signals (weather, social trends, promotions) and internal data – reducing forecast error by up to 50%.",
      },
      {
        title: "Dynamic Inventory Optimization",
        desc: "Reinforcement learning agents that continuously adjust safety stock levels and replenishment rules across multi-echelon networks.",
      },
      {
        title: "Supplier Risk Intelligence",
        desc: "NLP and graph-based models that monitor supplier financial health, geopolitical events, and quality data to predict disruptions before they occur.",
      },
      {
        title: "Autonomous Logistics & Routing",
        desc: "AI-powered route optimization that adapts to real-time traffic, weather, and capacity constraints – integrated with last-mile delivery platforms.",
      },
      {
        title: "Computer Vision for Warehousing",
        desc: "Automated quality inspection, cycle counting, and package dimensioning using camera feeds and edge AI.",
      },
      {
        title: "Supply Chain Control Tower",
        desc: "Unified dashboard with AI-generated alerts, what-if scenario simulation, and prescriptive recommendations for exception management.",
      },
    ],
    process: [
      {
        step: "01",
        title: "Discovery & Data Audit",
        desc: "Assess supply chain data sources, pain points, and AI opportunity areas.",
      },
      {
        step: "02",
        title: "AI Use Case Prioritization",
        desc: "Identify high-impact, feasible AI applications using value/complexity matrix.",
      },
      {
        step: "03",
        title: "Data Engineering",
        desc: "Build pipelines to unify ERP, WMS, TMS, and external data sources.",
      },
      {
        step: "04",
        title: "Model Development",
        desc: "Train, validate, and backtest ML models on historical supply chain data.",
      },
      {
        step: "05",
        title: "Integration & Pilot",
        desc: "Deploy AI models in shadow mode, then pilot in a single region or product line.",
      },
      {
        step: "06",
        title: "Scale & Optimize",
        desc: "Roll out across the enterprise with continuous retraining and MLOps.",
      },
    ],
    benefits: [
      "Reduce forecast error and eliminate manual planning cycles",
      "Lower working capital through optimized inventory",
      "Improve on-time in-full (OTIF) delivery performance",
      "Automate exception handling and root cause analysis",
      "Build resilience against supplier and logistics disruptions",
      "Unlock cost savings of 15-30% across logistics and warehousing",
    ],
    industries: [
      "Retail & E-commerce",
      "Manufacturing",
      "Pharmaceuticals",
      "Food & Beverage",
      "Automotive",
      "Consumer Electronics",
    ],
    technologies: [
      "Python",
      "TensorFlow",
      "PyTorch",
      "AWS SageMaker",
      "Azure ML",
      "Databricks",
      "IoT Sensors",
      "Computer Vision",
    ],
    faq: [
      {
        q: "How is AI supply chain different from traditional SCM?",
        a: "Traditional SCM relies on static rules and periodic forecasting. AI SCM uses real-time data, learns patterns automatically, and adapts dynamically – often without human intervention for routine decisions.",
      },
      {
        q: "Do we need to replace our existing ERP/WMS?",
        a: "No. We integrate AI models on top of your existing systems via APIs and data pipelines, so you keep your current infrastructure while adding intelligence.",
      },
    ],
  },

  {
    id: "data-analytics",
    slug: "data-analytics",
    title: "Data Analytics & Predictive Forecasting",
    tagline: "Turn Data Into Foresight",
    shortDesc:
      "Unlock the hidden value in your data with advanced analytics, interactive dashboards, and predictive models that drive smarter decisions.",
    heroImage:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&auto=format&fit=crop&q=80",
    icon: "📊",
    color: "sky",
    overview: {
      heading: "From Hindsight to Foresight",
      body: `Most organizations are drowning in data but starving for insights. Descriptive analytics tells you what happened. Predictive forecasting tells you what will happen. Prescriptive analytics tells you what to do about it.

AgileTech builds end-to-end data analytics solutions – from data warehousing and ETL/ELT pipelines to interactive BI dashboards and custom machine learning models for forecasting. Whether you need to predict customer churn, optimize pricing, forecast sales, or detect anomalies, we transform raw data into a strategic asset.`,
    },
    stats: [
      { value: "85%", label: "Faster Reporting Cycles" },
      { value: "3x", label: "ROI from Data Investments" },
      { value: "99.9%", label: "Data Accuracy Achieved" },
      { value: "500+", label: "Analytics Models Deployed" },
    ],
    features: [
      {
        title: "Data Warehousing & Lakehouse",
        desc: "Design and implement modern data platforms using Snowflake, BigQuery, Redshift, or Databricks – unifying structured and unstructured data.",
      },
      {
        title: "Interactive Dashboards & BI",
        desc: "Build self-service dashboards in Power BI, Tableau, Looker, or Superset that empower business users with real-time KPIs and drill-down exploration.",
      },
      {
        title: "Predictive Forecasting Models",
        desc: "Custom time-series forecasting (ARIMA, Prophet, LSTM) for sales, demand, inventory, cash flow, and workforce planning.",
      },
      {
        title: "Customer & Churn Analytics",
        desc: "Segment customers, predict lifetime value (LTV), and identify at-risk accounts using classification models – with actionable retention recommendations.",
      },
      {
        title: "Pricing & Promotion Optimization",
        desc: "Elasticity modeling, price sensitivity analysis, and promotion uplift measurement to maximize revenue and margin.",
      },
      {
        title: "Anomaly Detection & Monitoring",
        desc: "Automated detection of fraud, operational issues, or data quality problems using unsupervised learning and statistical process control.",
      },
    ],
    process: [
      {
        step: "01",
        title: "Data Strategy",
        desc: "Define key business questions, KPIs, and data sources.",
      },
      {
        step: "02",
        title: "Data Integration",
        desc: "Build pipelines to extract, transform, and load data from source systems.",
      },
      {
        step: "03",
        title: "Data Modeling",
        desc: "Design dimensional models, star schemas, or data vaults for analytics.",
      },
      {
        step: "04",
        title: "Dashboard & Report Development",
        desc: "Create interactive visualizations and scheduled reporting.",
      },
      {
        step: "05",
        title: "Predictive Model Training",
        desc: "Develop and validate forecasting models on historical data.",
      },
      {
        step: "06",
        title: "Deployment & Training",
        desc: "Publish dashboards, embed models into workflows, and train business users.",
      },
    ],
    benefits: [
      "Replace gut-feel decisions with data-backed confidence",
      "Reduce reporting effort from weeks to minutes",
      "Forecast future trends with measurable accuracy",
      "Identify cost-saving and revenue opportunities hidden in data",
      "Improve cross-functional alignment through a single source of truth",
      "Detect issues before they become critical",
    ],
    industries: [
      "Financial Services",
      "Retail",
      "Healthcare",
      "Logistics",
      "Telecommunications",
      "SaaS",
    ],
    technologies: [
      "Snowflake",
      "BigQuery",
      "Tableau",
      "Power BI",
      "Looker",
      "dbt",
      "Python",
      "R",
      "Apache Spark",
    ],
    faq: [
      {
        q: "Do we need a data warehouse before we can do predictive analytics?",
        a: "Not necessarily. We can start with data from your existing databases or even flat files. However, a well-structured data warehouse dramatically accelerates model development and reliability.",
      },
      {
        q: "How accurate are your forecasting models?",
        a: "Accuracy depends on data quality, volatility, and the forecast horizon. We typically achieve 85-95% accuracy for short-term forecasts (weeks to months) and 70-85% for longer-term, depending on the industry.",
      },
    ],
  },

  {
    id: "sap-consulting",
    slug: "sap-consulting",
    title: "SAP Consulting & Integration Services",
    tagline: "Unlock the Full Power of SAP",
    shortDesc:
      "Expert SAP consulting, implementation, and integration – from S/4HANA to cloud-ready architectures that align with your business processes.",
    heroImage:
      "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&auto=format&fit=crop&q=80",
    icon: "⚙️",
    color: "sky",
    overview: {
      heading: "SAP – The Digital Core of Your Enterprise",
      body: `SAP systems are the backbone of many large enterprises, but their complexity often leads to underutilization, siloed data, and costly customizations. AgileTech's SAP consulting practice bridges the gap between SAP's technical capabilities and your unique business needs.

We provide end-to-end services: greenfield S/4HANA implementations, ECC to S/4HANA migrations, custom ABAP/Fiori development, integration with non-SAP clouds (Salesforce, AWS, Workday), and ongoing managed services. Our certified consultants ensure your SAP landscape is clean, performant, and future-proof.`,
    },
    stats: [
      { value: "200+", label: "SAP Projects" },
      { value: "40%", label: "Faster Implementations" },
      { value: "98%", label: "On-Time Go-Live Rate" },
      { value: "60+", label: "Certified SAP Consultants" },
    ],
    features: [
      {
        title: "SAP S/4HANA Implementation",
        desc: "Full-cycle greenfield or brownfield implementations using SAP Activate methodology – from business blueprint to hypercare.",
      },
      {
        title: "ECC to S/4HANA Migration",
        desc: "Structured migration with minimal business disruption, including system conversion (SUM) or selective data transition.",
      },
      {
        title: "SAP Business Technology Platform (BTP)",
        desc: "Extend SAP with custom apps, integrations, and analytics using SAP BTP – including Integration Suite, Extension Suite, and SAP Analytics Cloud.",
      },
      {
        title: "SAP Integration Services",
        desc: "Connect SAP with Salesforce, Workday, AWS, Azure, and legacy systems using CPI, SAP PI/PO, or API management.",
      },
      {
        title: "SAP Fiori & UX Modernization",
        desc: "Design and deploy role-based Fiori apps, launchpads, and custom UI5/Lightning Web Components to improve user adoption.",
      },
      {
        title: "SAP Managed Services (AMS)",
        desc: "24/7 application management, basis administration, security patching, and continuous improvement with SLAs.",
      },
    ],
    process: [
      {
        step: "01",
        title: "Discovery & Assessment",
        desc: "As-is analysis, fit-gap assessment, and project sizing.",
      },
      {
        step: "02",
        title: "Blueprint & Design",
        desc: "Business process workshops, configuration workbooks, and integration design.",
      },
      {
        step: "03",
        title: "Realization (Build)",
        desc: "System configuration, custom development, and data migration planning.",
      },
      {
        step: "04",
        title: "Testing",
        desc: "Unit, integration, UAT, and performance testing with automated test scripts.",
      },
      {
        step: "05",
        title: "Go-Live & Hypercare",
        desc: "Cutover execution, production deployment, and intensive post-go-live support.",
      },
      {
        step: "06",
        title: "Run & Optimize",
        desc: "AMS, continuous improvement, and roadmap advisory.",
      },
    ],
    benefits: [
      "Real-time visibility across finance, supply chain, HR, and sales",
      "Reduced TCO through clean core principles and cloud readiness",
      "Seamless integration with best-of-breed SaaS applications",
      "Improved user productivity with modern Fiori interfaces",
      "Faster month-end close and regulatory compliance",
      "Scalable platform for future AI and IoT initiatives",
    ],
    industries: [
      "Manufacturing",
      "Professional Services",
      "Public Sector",
      "Utilities",
      "Oil & Gas",
      "Life Sciences",
    ],
    technologies: [
      "SAP S/4HANA",
      "SAP BTP",
      "SAP Fiori",
      "ABAP",
      "SAP CPI",
      "SAP SuccessFactors",
      "SAP Ariba",
    ],
    faq: [
      {
        q: "What is the difference between greenfield and brownfield S/4HANA?",
        a: "Greenfield is a fresh implementation (best for process reengineering). Brownfield migrates your existing ECC system (faster but carries over legacy debt). We help you choose based on your business goals.",
      },
      {
        q: "Can you integrate SAP with non-SAP cloud systems?",
        a: "Yes. We have deep experience integrating SAP with Salesforce, AWS, Azure, Google Cloud, Workday, and many other platforms using both SAP's native tools and middleware like MuleSoft.",
      },
    ],
  },

  {
    id: "salesforce-solutions",
    slug: "salesforce-solutions",
    title: "Salesforce Solutions",
    tagline: "CRM Excellence for Customer-Centric Growth",
    shortDesc:
      "Design, implement, and optimize Salesforce to unify sales, service, and marketing – delivering a 360° customer view and accelerating revenue.",
    heroImage:
      "https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200&auto=format&fit=crop&q=80",
    icon: "☁️",
    color: "sky",
    overview: {
      heading: "Salesforce – More Than a CRM",
      body: `Salesforce is the world's leading customer relationship platform, but its true power emerges only when configured, extended, and adopted correctly. AgileTech's certified Salesforce experts help you leverage Sales Cloud, Service Cloud, Marketing Cloud, and the Salesforce Platform to drive measurable business outcomes.

Whether you're a startup implementing Salesforce for the first time or an enterprise with a complex, underperforming org, we provide strategic guidance, technical execution, and change management to ensure you get maximum ROI from your Salesforce investment.`,
    },
    stats: [
      { value: "300+", label: "Salesforce Projects" },
      { value: "45%", label: "Increase in Sales Productivity" },
      { value: "50%", label: "Faster Case Resolution" },
      { value: "70+", label: "Certified Salesforce Professionals" },
    ],
    features: [
      {
        title: "Sales Cloud Implementation",
        desc: "Lead management, opportunity scoring, CPQ, territory management, and forecasting – tailored to your sales process.",
      },
      {
        title: "Service Cloud & Field Service",
        desc: "Omnichannel case management, knowledge base, chatbots, and Field Service Lightning for mobile workforces.",
      },
      {
        title: "Marketing Cloud & Pardot",
        desc: "Customer journey orchestration, email personalization, B2B account-based marketing, and ROI attribution.",
      },
      {
        title: "Salesforce Einstein AI",
        desc: "Predictive lead scoring, opportunity insights, automated activity capture, and next-best-action recommendations.",
      },
      {
        title: "Custom Apps (Apex & LWC)",
        desc: "Build custom Lightning Web Components, Apex triggers, and flows to address unique business logic not covered by standard Salesforce.",
      },
      {
        title: "Integration & MuleSoft",
        desc: "Connect Salesforce to ERP, billing systems, data warehouses, and external APIs using MuleSoft or native connectors.",
      },
    ],
    process: [
      {
        step: "01",
        title: "Discovery & Process Mapping",
        desc: "Workshops to map sales/service processes and identify gaps.",
      },
      {
        step: "02",
        title: "Solution Architecture",
        desc: "Data model design, security model, and integration architecture.",
      },
      {
        step: "03",
        title: "Configuration & Development",
        desc: "Declarative configuration, Apex, LWC, and automation flows.",
      },
      {
        step: "04",
        title: "Data Migration",
        desc: "Cleanse, deduplicate, and migrate data from legacy CRM or spreadsheets.",
      },
      {
        step: "05",
        title: "Testing & UAT",
        desc: "Unit, integration, and user acceptance testing with sandbox environments.",
      },
      {
        step: "06",
        title: "Training & Go-Live",
        desc: "Role-based training, adoption plan, and post-launch support.",
      },
    ],
    benefits: [
      "Single source of truth for all customer interactions",
      "Automated lead routing and follow-ups – no more dropped leads",
      "Accurate sales forecasting and pipeline visibility",
      "Faster case resolution with AI-powered knowledge recommendations",
      "Personalized marketing campaigns driven by real-time data",
      "Reduced administrative burden with workflow automation",
    ],
    industries: [
      "Financial Services",
      "Healthcare",
      "Technology",
      "Retail",
      "Non-Profit",
      "Manufacturing",
    ],
    technologies: [
      "Sales Cloud",
      "Service Cloud",
      "Marketing Cloud",
      "Einstein AI",
      "MuleSoft",
      "Apex",
      "Lightning Web Components",
      "Tableau CRM",
    ],
    faq: [
      {
        q: "How long does a typical Salesforce implementation take?",
        a: "A basic Sales Cloud implementation takes 8-12 weeks. Complex multi-cloud projects with custom development can take 4-6 months. We use agile sprints to deliver early value.",
      },
      {
        q: "Our Salesforce org is a mess. Can you fix it?",
        a: "Absolutely. We specialize in org health assessments, data cleanup, technical debt remediation, and user adoption rescue programs.",
      },
    ],
  },

  {
    id: "cloud-migration",
    slug: "cloud-migration",
    title: "Cloud Migration & Modernization",
    tagline: "Move to the Cloud with Confidence",
    shortDesc:
      "Migrate your workloads to AWS, Azure, or GCP with zero downtime, reduced costs, and a clear path to cloud-native modernization.",
    heroImage:
      "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=1200&auto=format&fit=crop&q=80",
    icon: "☁️",
    color: "sky",
    overview: {
      heading: "Cloud Migration Done Right",
      body: `Cloud migration is not just about lifting and shifting servers. It's an opportunity to modernize your applications, improve security, and reduce costs – but only if executed with a proven methodology.

AgileTech has migrated hundreds of workloads to public cloud platforms. We use the 6Rs framework (Rehost, Replatform, Refactor, Repurchase, Retire, Retain) to choose the right strategy for each workload. From assessment and pilot to full-scale migration and FinOps optimization, we ensure your cloud journey delivers on its promise of agility and savings.`,
    },
    stats: [
      { value: "400+", label: "Cloud Migrations" },
      { value: "55%", label: "Average Infrastructure Cost Savings" },
      { value: "99.99%", label: "Post-Migration Uptime" },
      { value: "0", label: "Data Loss Incidents" },
    ],
    features: [
      {
        title: "Cloud Readiness Assessment",
        desc: "Detailed inventory of workloads, dependencies, TCO analysis, and migration wave planning.",
      },
      {
        title: "AWS Migration",
        desc: "Lift-and-shift, re-platforming, or refactoring on AWS using Migration Hub, Application Discovery Service, and AWS DMS.",
      },
      {
        title: "Azure Migration",
        desc: "Migrate Windows/.NET workloads, SQL Server, and VMware environments to Azure with Azure Migrate.",
      },
      {
        title: "GCP Migration",
        desc: "Modernize data-intensive and AI/ML workloads on Google Cloud with Transfer Appliance and Migrate for Compute Engine.",
      },
      {
        title: "Application Modernization",
        desc: "Refactor monoliths into microservices, containerize with Docker/Kubernetes, and adopt serverless functions.",
      },
      {
        title: "FinOps & Cost Optimization",
        desc: "Implement tagging, rightsizing, reserved instances, and automated shutdowns to control cloud spend.",
      },
    ],
    process: [
      {
        step: "01",
        title: "Discovery & Assessment",
        desc: "Workload inventory, dependency mapping, and TCO modeling.",
      },
      {
        step: "02",
        title: "Migration Strategy",
        desc: "Define 6R per workload and build migration runbooks.",
      },
      {
        step: "03",
        title: "Landing Zone Setup",
        desc: "Build secure, compliant foundation accounts and networking.",
      },
      {
        step: "04",
        title: "Pilot Migration",
        desc: "Migrate a low-risk workload to validate processes.",
      },
      {
        step: "05",
        title: "Wave Migrations",
        desc: "Execute waves with automated tools, rollback plans, and validation.",
      },
      {
        step: "06",
        title: "Optimize & Govern",
        desc: "Implement FinOps, security guardrails, and continuous improvement.",
      },
    ],
    benefits: [
      "Eliminate data center capital expenditures",
      "Scale resources up or down in minutes",
      "Improve disaster recovery with geo-redundancy",
      "Enable remote and hybrid workforces",
      "Access cloud-native services (AI/ML, serverless, managed databases)",
      "Pay only for what you use",
    ],
    industries: [
      "Financial Services",
      "Healthcare",
      "Government",
      "Retail",
      "Media & Entertainment",
      "Manufacturing",
    ],
    technologies: [
      "AWS",
      "Microsoft Azure",
      "Google Cloud Platform",
      "Kubernetes",
      "Docker",
      "Terraform",
      "Ansible",
    ],
    faq: [
      {
        q: "What is the difference between lift-and-shift and refactoring?",
        a: "Lift-and-shift moves workloads as-is to cloud VMs (fast but less cost-optimized). Refactoring rewrites applications to use cloud-native services (longer but better long-term value). We help you decide per workload.",
      },
      {
        q: "How do you ensure minimal downtime?",
        a: "We use blue-green deployments, database replication, and cutover windows typically under 4 hours. For critical systems, we design zero-downtime migrations using active-active or parallel run strategies.",
      },
    ],
  },

  {
    id: "ai-automation",
    slug: "ai-automation",
    title: "Artificial Intelligence & Automation",
    tagline: "Intelligent Automation for the Modern Enterprise",
    shortDesc:
      "Combine AI, machine learning, and robotic process automation (RPA) to automate complex workflows, reduce manual effort, and unlock new levels of efficiency.",
    heroImage:
      "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&auto=format&fit=crop&q=80",
    icon: "🤖",
    color: "sky",
    overview: {
      heading: "Beyond RPA – Intelligent Automation",
      body: `Robotic Process Automation (RPA) handles rule-based, repetitive tasks. Artificial Intelligence handles unstructured data, predictions, and decisions. Together, they form Intelligent Automation – a game-changer for operational efficiency.

AgileTech builds end-to-end automation solutions that blend UiPath, Power Automate, or Automation Anywhere with custom AI models (document understanding, NLP, computer vision, decision engines). From invoice processing and customer onboarding to claims adjudication and report generation, we automate what matters.`,
    },
    stats: [
      { value: "70%", label: "Average Manual Effort Reduction" },
      { value: "95%", label: "Faster Process Execution" },
      { value: "100+", label: "Automation Bots Deployed" },
      { value: "99%", label: "Accuracy on Unstructured Documents" },
    ],
    features: [
      {
        title: "Robotic Process Automation (RPA)",
        desc: "Deploy software bots to automate data entry, screen scraping, report generation, and system-to-system data movement using UiPath, Power Automate, or Blue Prism.",
      },
      {
        title: "Intelligent Document Processing (IDP)",
        desc: "Extract, classify, and validate data from invoices, contracts, forms, and emails using OCR + NLP models – even from poor-quality scans.",
      },
      {
        title: "AI Decision Engines",
        desc: "Embed ML models into automation workflows to make real-time decisions – e.g., approve/deny claims, route customer tickets, or prioritize tasks.",
      },
      {
        title: "Conversational AI & Chatbots",
        desc: "Build NLP-powered chatbots and voice assistants that handle customer inquiries, employee HR requests, or IT support tickets automatically.",
      },
      {
        title: "Process Mining & Discovery",
        desc: "Analyze event logs from your systems to identify automation opportunities and baseline process performance.",
      },
      {
        title: "Automation Orchestration",
        desc: "Centralized control room to schedule, monitor, and manage all bots – with role-based access, audit logs, and exception handling.",
      },
    ],
    process: [
      {
        step: "01",
        title: "Automation Discovery",
        desc: "Identify high-volume, rule-based, or repetitive processes using process mining and workshops.",
      },
      {
        step: "02",
        title: "Feasibility & ROI Analysis",
        desc: "Estimate automation effort, cost savings, and implementation roadmap.",
      },
      {
        step: "03",
        title: "Bot Development",
        desc: "Build RPA workflows and AI models in parallel, with reusable components.",
      },
      {
        step: "04",
        title: "Testing in Sandbox",
        desc: "Run bots in a controlled environment with realistic data volumes.",
      },
      {
        step: "05",
        title: "Deployment & Monitoring",
        desc: "Deploy to production with attended or unattended bot modes and real-time dashboards.",
      },
      {
        step: "06",
        title: "Continuous Improvement",
        desc: "Monitor exception rates, retrain AI models, and expand automation to adjacent processes.",
      },
    ],
    benefits: [
      "Free employees from repetitive manual work",
      "Process documents 10x faster with near-zero errors",
      "Scale operations without hiring more headcount",
      "Improve compliance with auditable bot logs",
      "Accelerate response times to customers and employees",
      "Combine AI judgment with RPA speed for end-to-end automation",
    ],
    industries: [
      "Financial Services",
      "Healthcare",
      "Insurance",
      "Logistics",
      "Legal",
      "Customer Service",
    ],
    technologies: [
      "UiPath",
      "Microsoft Power Automate",
      "Automation Anywhere",
      "OpenAI GPT",
      "LangChain",
      "Python",
      "OCR (Tesseract, Document AI)",
    ],
    faq: [
      {
        q: "What is the difference between RPA and AI automation?",
        a: "RPA automates structured, rule-based tasks (e.g., copy-pasting data between systems). AI automation adds the ability to understand unstructured data (documents, images, language) and make intelligent decisions.",
      },
      {
        q: "How do we know which processes to automate first?",
        a: "We use a prioritization matrix based on process volume, rule-based nature, stability, and ROI. Typical first candidates are invoice processing, data entry, report generation, and customer onboarding.",
      },
    ],
  },
];

export default servicesData;
